import { useState } from "react";
import { Blocks } from "lucide-react";
import type { PublicQuote } from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { BlockAddMenu } from "./BlockAddMenu";
import { BlockPalette } from "./BlockPalette";
import { DocumentFormatToolbar } from "./DocumentFormatToolbar";
import { ColumnResizeHandles } from "./ColumnResizeHandles";
import { EditorOverlay } from "./EditorOverlay";
import { FieldTooltip } from "./FieldTooltip";
import { InlineTextEditOverlay } from "./InlineTextEditOverlay";
import { QuoteDocumentRenderer } from "./QuoteDocumentRenderer";
import type { BlockInsertPosition } from "./documentEditor";
import type { OverlayRect } from "./overlayGeometry";
import type { DocumentAssetUrls } from "./types";
import type { DocumentEditorApi } from "./useDocumentEditor";

/** The open hover "+" menu: where it hangs, and which side it inserts on. */
interface AddMenuTarget {
  position: BlockInsertPosition;
  anchorRect: OverlayRect;
}

interface DocumentEditorPanesProps {
  /** The editor driving the canvas, palette, and toolbar. */
  editor: DocumentEditorApi;
  /** The quote projection the canvas binds. */
  quote: PublicQuote;
  /** Resolved signed asset urls keyed by asset id. */
  assetUrls: DocumentAssetUrls;
  /** The org logo url, when it is not the bound quote's own. */
  orgLogoUrl?: string | null;
  /** Registers an uploaded asset id and file so its preview outlives the editor. */
  onLocalAsset: (assetId: string, file: File) => void;
  /** True while an image upload is in flight, blocking canvas interaction. */
  isUploadingImage: boolean;
  /** Called when an image upload starts or finishes. */
  onUploadingChange: (uploading: boolean) => void;
}

/**
 * The editor's body: the editable canvas, centered under its persistent
 * formatting bar, which carries the whole block inspector - style knobs, the
 * "Properties" popover, and the block operations. The block palette stays
 * collapsed in a left sheet behind the bar's "Blocks" toggle, so the page is
 * never pushed off-center at any width. The same palette opens as a popover
 * beside either "+" that hovering a block puts on its edges, which selects that
 * block and aims the insert at the edge clicked. Shared by the per-quote and template
 * editors, which differ only in their header and how they persist.
 * @param editor - The editor driving the canvas, palette, and toolbar
 * @param quote - The quote projection the canvas binds
 * @param assetUrls - Resolved signed asset urls keyed by asset id
 * @param orgLogoUrl - The org logo url, overriding the bound quote's own
 * @param onLocalAsset - Registers an uploaded asset id and file
 * @param isUploadingImage - True while an image upload is in flight
 * @param onUploadingChange - Called when an image upload starts or finishes
 * @returns The editor body
 */
export function DocumentEditorPanes({
  editor,
  quote,
  assetUrls,
  orgLogoUrl,
  onLocalAsset,
  isUploadingImage,
  onUploadingChange,
}: DocumentEditorPanesProps) {
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [addMenu, setAddMenu] = useState<AddMenuTarget | null>(null);

  return (
    <main className="relative flex flex-1 min-h-0">
      <Sheet open={isPaletteOpen} onOpenChange={setIsPaletteOpen}>
        <SheetContent
          side="left"
          className="w-72 overflow-y-auto p-0"
          aria-describedby={undefined}
        >
          <SheetTitle className="sr-only">Insert blocks</SheetTitle>
          <BlockPalette
            documentHtml={editor.documentHtml}
            onInsert={(blockHtml) => {
              editor.insertBlock(blockHtml);
              setIsPaletteOpen(false);
            }}
          />
        </SheetContent>
      </Sheet>
      <section className="relative flex flex-1 min-w-0 flex-col min-h-0">
        <div className="flex shrink-0 flex-wrap items-center gap-1 border-b bg-background px-2 py-1.5">
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => setIsPaletteOpen(true)}
          >
            <Blocks className="size-4" />
            Blocks
          </Button>
          <Separator orientation="vertical" className="mx-1 h-5" />
          <DocumentFormatToolbar
            editor={editor}
            disabled={isUploadingImage}
            onLocalAsset={onLocalAsset}
            onUploadingChange={onUploadingChange}
          />
        </div>
        <div
          ref={editor.previewRef}
          className="relative flex-1 min-h-0 overflow-auto bg-muted [&_::selection]:bg-blue-500/30 [&_[data-field-title]]:rounded-[3px] [&_[data-field-title]]:transition-colors [&_[data-field-title]]:duration-150 [&_[data-field-title]:hover]:bg-blue-500/10"
          {...editor.previewHandlers}
        >
          <QuoteDocumentRenderer
            documentHtml={editor.renderedHtml}
            quote={quote}
            assetUrls={assetUrls}
            orgLogoUrl={orgLogoUrl}
            mode="view"
            editable
          />
          <FieldTooltip containerRef={editor.previewRef} />
          <EditorOverlay
            containerRef={editor.previewRef}
            selectedNodeId={editor.selection?.nodeId ?? null}
            label={editor.selection?.label ?? null}
            outlineSingleInstance={
              editor.selection?.repeatsOnEveryPage ?? false
            }
            clickedInstanceRef={editor.clickedInstanceRef}
            hoveredNodeId={editor.hoveredNodeId}
            hoverOutlineSingleInstance={editor.hoverOutlinesSingleInstance}
            hoverChromeNodeId={editor.hoverChromeNodeId}
            hoveredInstanceRef={editor.hoveredInstanceRef}
            isDragging={editor.isDragging}
            onDragStart={editor.startBlockDrag}
            dropIndicator={editor.dropIndicator}
            onAddContent={(nodeId, position, anchorRect) => {
              editor.selectNode(nodeId);
              setAddMenu({ position, anchorRect });
            }}
          />
          {addMenu && (
            <BlockAddMenu
              anchorRect={addMenu.anchorRect}
              documentHtml={editor.documentHtml}
              onInsert={(blockHtml) => {
                editor.insertBlock(blockHtml, addMenu.position);
                setAddMenu(null);
              }}
              onClose={() => setAddMenu(null)}
            />
          )}
          <ColumnResizeHandles
            containerRef={editor.previewRef}
            tableNodeId={editor.selection?.tableNodeId ?? null}
            columnsNodeId={editor.selection?.columns?.nodeId ?? null}
            onResize={editor.resizeColumn}
            onResizeColumns={editor.resizeColumnsColumn}
          />
          {editor.textEdit && (
            <InlineTextEditOverlay
              key={editor.textEdit.id}
              containerRef={editor.previewRef}
              element={editor.textEdit.element}
              initialDoc={editor.textEdit.initialDoc}
              blocksEnabled={editor.textEdit.blocksEnabled}
              initialSelection={editor.textEdit.initialSelection}
              initialCaret={editor.textEdit.initialCaret}
              quote={quote}
              registerControls={editor.registerTextEditControls}
              onCommit={editor.commitTextEdit}
              onCancel={editor.cancelTextEdit}
              onDraftChange={editor.onTextDraftChange}
              onElementDetached={editor.onTextElementDetached}
              onBackspaceWhenEmpty={editor.onTextBackspaceWhenEmpty}
            />
          )}
        </div>
      </section>
      {isUploadingImage && (
        <div
          className="absolute inset-0 z-20 cursor-progress bg-background/40"
          aria-hidden
        />
      )}
    </main>
  );
}
